import React, { useState, useEffect, useCallback, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import { User, Organization, UserFormData, formatErrorMessage } from '../types';
import { useStrictModeGuard } from '../hooks/useStrictModeGuard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSave, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import Pagination from './Pagination';

const UserManagement: React.FC = () => {
  const { executeOnce } = useStrictModeGuard();
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    username: '',
    password: '',
    organization_id: undefined,
    role: 'organization_user'
  });

  const fetchUsers = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await axios.get('/users', {
        params: { skip, limit: itemsPerPage }
      });
      const responseData = response.data;
      const usersData = responseData.items || responseData; // Handle both old and new format
      
      setUsers(usersData);
      
      // Update pagination info from response
      if (responseData.total_count !== undefined) {
        setTotalItems(responseData.total_count);
        setTotalPages(responseData.total_pages);
      }
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  const fetchOrganizations = useCallback(async (): Promise<void> => {
    try {
      const response = await axios.get('/organizations');
      // The API returns { items: Organization[], total_count: number, ... }
      const organizationsData = response.data?.items || [];
      setOrganizations(organizationsData);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      // Set empty array on error to prevent map() errors
      setOrganizations([]);
    }
  }, []);

  // Fetch organizations once on mount
  useEffect(() => {
    executeOnce(() => {
      fetchOrganizations();
    });
  }, [fetchOrganizations, executeOnce]);

  // Fetch users when page changes
  useEffect(() => {
    fetchUsers();
  }, [currentPage, fetchUsers]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? (value ? parseInt(value) : undefined) : value
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (editingUser) {
        // Don't send password when editing
        delete submitData.password;
      }
      if (submitData.organization_id === undefined) {
        delete submitData.organization_id;
      }

      if (editingUser) {
        await axios.put<User>(`/users/${editingUser.id}`, submitData);
      } else {
        await axios.post<User>('/users', submitData);
      }

      await fetchUsers();
      resetForm();
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await axios.delete(`/users/${id}`);
      await fetchUsers();
    } catch (err) {
      setError(formatErrorMessage(err, 'An error occurred'));
    }
  };

  const handleEdit = (user: User): void => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      username: user.username,
      password: '', // Password is not pre-filled for security
      organization_id: user.organization_id || undefined,
      role: user.role
    });
    setShowCreateForm(true);
  };

  const resetForm = (): void => {
    setFormData({
      email: '',
      username: '',
      password: '',
      organization_id: undefined,
      role: 'organization_user'
    });
    setShowCreateForm(false);
    setEditingUser(null);
    setError(null);
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: '16px', color: '#dc2626' }}>Loading users...</div>;
  if (error) return <div style={{ textAlign: 'center', marginTop: '16px', color: '#dc2626' }}>Error: {error}</div>;

  return (
    <div style={{ padding: '24px' }}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '24px' }}>User Management</h2>

      {/* Add/Edit User Form */}
      {showCreateForm && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '500', color: '#374151', marginBottom: '16px' }}>{editingUser ? 'Edit User' : 'Add New User'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="username" className="form-label">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            {!editingUser && (
              <div className="form-group">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
            )}
            <div className="form-group">
              <label htmlFor="organization_id" className="form-label">Organization</label>
              <select
                id="organization_id"
                name="organization_id"
                value={formData.organization_id || ''}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">No Organization</option>
                {(organizations || []).map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="role" className="form-label">Role</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="form-input"
              >
                <option value="organization_user">Organization User</option>
                <option value="organization_admin">Organization Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <button
                type="submit"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#16a34a',
                  border: '2px solid white',
                  borderRadius: '8px',
                  color: 'white',
                  padding: '0',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  minWidth: '140px',
                  minHeight: '40px'
                }}
              >
                <div style={{
                  backgroundColor: '#16a34a',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '1px solid rgba(255,255,255,0.3)'
                }}>
                  <FontAwesomeIcon icon={editingUser ? faSave : faPlus} style={{ fontSize: '14px' }} />
                </div>
                <div style={{
                  padding: '10px 12px',
                  flex: 1,
                  textAlign: 'center'
                }}>
                  {editingUser ? 'UPDATE USER' : 'ADD USER'}
                </div>
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#6b7280',
                  border: '2px solid white',
                  borderRadius: '8px',
                  color: 'white',
                  padding: '0',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  minWidth: '100px',
                  minHeight: '40px'
                }}
              >
                <div style={{
                  padding: '10px 12px',
                  flex: 1,
                  textAlign: 'center'
                }}>
                  CANCEL
                </div>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add User Button */}
      {!showCreateForm && (
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{ 
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#2563eb',
              border: '2px solid white',
              borderRadius: '8px',
              color: 'white',
              padding: '0',
              fontSize: '14px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              cursor: 'pointer',
              overflow: 'hidden',
              minWidth: '160px',
              minHeight: '40px'
            }}
          >
            <div style={{
              backgroundColor: '#2563eb',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRight: '1px solid rgba(255,255,255,0.3)'
            }}>
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: '14px' }} />
            </div>
            <div style={{
              padding: '10px 12px',
              flex: 1,
              textAlign: 'center'
            }}>
              ADD NEW USER
            </div>
          </button>
        </div>
      )}

      {/* Users List */}
      <div className="card">
        <h3 style={{ fontSize: '1.25rem', fontWeight: '500', color: '#374151', marginBottom: '16px' }}>All Users</h3>
        {(users || []).length === 0 ? (
          <p style={{ color: '#6b7280' }}>No users found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Username</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Organization</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Role</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '500' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(users || []).map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px' }}>{user.username}</td>
                    <td style={{ padding: '12px' }}>{user.email}</td>
                    <td style={{ padding: '12px' }}>
                      {user.organization ? user.organization.name : 'No Organization'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {user.role === 'super_admin' ? (
                        <span style={{ 
                          backgroundColor: '#fef3c7', 
                          color: '#d97706', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px' 
                        }}>
                          Super Admin
                        </span>
                      ) : user.role === 'organization_admin' ? (
                        <span style={{ 
                          backgroundColor: '#dbeafe', 
                          color: '#1d4ed8', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px' 
                        }}>
                          Org Admin
                        </span>
                      ) : (
                        <span style={{ 
                          backgroundColor: '#f3f4f6', 
                          color: '#374151', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px' 
                        }}>
                          User
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ 
                        backgroundColor: user.is_active ? '#dcfce7' : '#fee2e2', 
                        color: user.is_active ? '#166534' : '#dc2626', 
                        padding: '2px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px' 
                      }}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleEdit(user)}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#f59e0b',
                            border: '2px solid white',
                            borderRadius: '6px',
                            color: 'white',
                            padding: '0',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            minWidth: '60px',
                            minHeight: '28px'
                          }}
                        >
                          <div style={{
                            backgroundColor: '#f59e0b',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRight: '1px solid rgba(255,255,255,0.3)'
                          }}>
                            <FontAwesomeIcon icon={faEdit} style={{ fontSize: '10px' }} />
                          </div>
                          <div style={{
                            padding: '4px 6px',
                            flex: 1,
                            textAlign: 'center'
                          }}>
                            EDIT
                          </div>
                        </button>
                        {user.role !== 'super_admin' && (
                          <button
                            onClick={() => handleDelete(user.id)}
                            style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: '#dc2626',
                              border: '2px solid white',
                              borderRadius: '6px',
                              color: 'white',
                              padding: '0',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              cursor: 'pointer',
                              overflow: 'hidden',
                              minWidth: '70px',
                              minHeight: '28px'
                            }}
                          >
                            <div style={{
                              backgroundColor: '#dc2626',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRight: '1px solid rgba(255,255,255,0.3)'
                            }}>
                              <FontAwesomeIcon icon={faTrash} style={{ fontSize: '10px' }} />
                            </div>
                            <div style={{
                              padding: '4px 6px',
                              flex: 1,
                              textAlign: 'center'
                            }}>
                              DELETE
                            </div>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {(users || []).length > 0 && (
          <div className="card">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              showInfo={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
